import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import LocationSelector from './LocationSelector';
import FiscalPrinterButton from './FiscalPrinterButton';
import api from '../../services/api';

// Mapowanie sciezek na tytuly stron
const PAGE_TITLES = {
  '/': 'Kasa POS',
  '/pos': 'Kasa POS',
  '/products': 'Produkty',
  '/customers': 'Klienci',
  '/orders': 'Zamowienia',
  '/shipping': 'Wysylka',
  '/tasks': 'Zadania',
  '/wiadomosci': 'Wiadomosci',
  '/coupons': 'Kupony',
  '/sales-invoices': 'Faktury sprzedazy',
  '/purchase-invoices': 'Faktury zakupowe',
  '/warehouse': 'Magazyn',
  '/kasa-bank': 'Kasa / Bank',
  '/accounting': 'Ksiegowosc',
  '/reports': 'Raporty',
  '/locations': 'Lokalizacje',
  '/location-pricing': 'Ceny / Cenowki',
  '/settings': 'Ustawienia',
  '/admin': 'Panel Administracyjny',
  '/kolektor': 'Kolektor',
  '/external-costs': 'Koszty zewnetrzne',
  '/profitability': 'Rentownosc',
};

const Topbar = ({ onOpenChangePassword }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [latestMessage, setLatestMessage] = useState(null);
  const previousUnreadRef = useRef(-1);
  const lastSeenMessageIdRef = useRef(null);
  const notificationTimeoutRef = useRef(null);
  const isFirstLoadRef = useRef(true);

  // Tytul aktualnej strony
  const pageTitle = PAGE_TITLES[location.pathname] || 'V3SYS';
  const isPos = location.pathname === '/pos';

  // Pobieraj liczbe nieprzeczytanych wiadomosci co 10 sekund
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user?.login) return;

      try {
        const params = new URLSearchParams();
        params.append('user_login', user.login);
        if (user.location_id) {
          params.append('location_id', user.location_id);
        }

        const response = await api.get(`/messages/unread-count?${params}`);
        const data = response.data;

        if (data.success && data.data) {
          const newCount = data.data.unread_count || 0;
          const latestMsg = data.data.latest_message;

          const shouldShowNotification =
            (isFirstLoadRef.current && newCount > 0) ||
            (newCount > previousUnreadRef.current && previousUnreadRef.current >= 0) ||
            (latestMsg && latestMsg.id !== lastSeenMessageIdRef.current && newCount > 0);

          if (shouldShowNotification && latestMsg) {
            setLatestMessage(latestMsg);
            lastSeenMessageIdRef.current = latestMsg.id;
            setShowNotification(true);

            if (notificationTimeoutRef.current) {
              clearTimeout(notificationTimeoutRef.current);
            }

            notificationTimeoutRef.current = setTimeout(() => {
              setShowNotification(false);
            }, 10000);

            // Dzwiek powiadomienia
            try {
              const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH+Onp+fl5WSlIuBfXyImp6dnJiXlpSMf3aBjpycnJeVlpSNgXV+jZydnJiXlpORgnR+jJycnJiXl5SQgnd+ipycnJiWl5SQg3h/iZucnJiXl5WQhHl/h5qbnJiXl5WRhXuAhpmam5iXl5WRhnyBhZiZmpqXl5aSh36DhJaYmZqXl5aSiICFg5WXmJmXl5aTiYKHgpOWl5iXlpaTioSJgZGVlpeXlpWTjIeNfo2TlJWVlJSTjYmPfouSlJSUk5OSkYqRfYmQk5OTk5KSkYuTfIeOkpKSkZGRkIyUe4WMkZGRkJCQj42Ve4OKj5CQj4+Pjoy');
              audio.volume = 0.4;
              audio.play().catch(() => {});
            } catch (e) {}
          }

          isFirstLoadRef.current = false;
          previousUnreadRef.current = newCount;
          setUnreadMessages(newCount);
        }
      } catch (error) {
        // Error fetching unread count - silent
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 10000);

    return () => {
      clearInterval(interval);
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, [user]);

  // Zamknij powiadomienie
  const closeNotification = () => {
    setShowNotification(false);
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
  };

  // Przejdz do wiadomosci
  const goToMessages = () => {
    closeNotification();
    navigate('/wiadomosci');
  };

  const handleLogout = async () => {
    try {
      const result = await logout();
      if (result.success) {
        window.location.href = '/login';
      } else {
        toast.error('Blad podczas wylogowywania');
      }
    } catch (error) {
      toast.error('Blad polaczenia z serwerem');
    }
  };

  return (
    <>
      {/* CSS Animations for notification */}
      <style>
        {`
          @keyframes slideInFromRight {
            0% { transform: translateX(100%); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }
          @keyframes glow {
            0%, 100% { box-shadow: 0 0 5px rgba(34, 197, 94, 0.5); }
            50% { box-shadow: 0 0 20px rgba(34, 197, 94, 0.9), 0 0 30px rgba(34, 197, 94, 0.5); }
          }
          @keyframes shake {
            0%, 100% { transform: rotate(0deg); }
            10%, 30%, 50%, 70%, 90% { transform: rotate(-5deg); }
            20%, 40%, 60%, 80% { transform: rotate(5deg); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
          @keyframes urgentBlink {
            0%, 49%   { color: #dc3545; transform: scale(1.15) rotate(-6deg); text-shadow: 0 0 8px rgba(220,53,69,0.9); }
            50%, 100% { color: #fff;    transform: scale(1)    rotate(6deg);  text-shadow: 0 0 4px rgba(220,53,69,0.6); }
          }
          @keyframes urgentBg {
            0%, 49%   { background: rgba(220, 53, 69, 0.85); box-shadow: 0 0 14px rgba(220,53,69,0.85), inset 0 0 6px rgba(255,255,255,0.3); }
            50%, 100% { background: rgba(220, 53, 69, 0.25); box-shadow: 0 0 4px rgba(220,53,69,0.4); }
          }
          @keyframes urgentDot {
            0%, 100% { transform: scale(1);   background: #dc3545; box-shadow: 0 0 4px rgba(220,53,69,0.8); }
            50%      { transform: scale(1.6); background: #ff0; box-shadow: 0 0 10px rgba(255,235,0,0.9); }
          }
        `}
      </style>

      {/* Floating Message Notification Toast */}
      {showNotification && (
        <div
          onClick={goToMessages}
          style={{
            position: 'fixed',
            top: '70px',
            right: '20px',
            width: '340px',
            background: 'var(--card-bg)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
            zIndex: 9999,
            cursor: 'pointer',
            animation: 'slideInFromRight 0.5s ease-out, glow 2s ease-in-out infinite',
            overflow: 'hidden',
            border: '2px solid var(--primary)'
          }}
        >
          {/* Header */}
          <div style={{
            background: 'var(--primary)',
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: '#fff'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="fas fa-envelope" style={{ fontSize: '16px', animation: 'shake 0.5s ease-in-out' }} />
              <div>
                <div style={{ fontWeight: 700, fontSize: '13px' }}>Nowa wiadomosc!</div>
                <div style={{ fontSize: '10px', opacity: 0.9 }}>
                  {unreadMessages > 1 ? `Masz ${unreadMessages} nieprzeczytanych` : 'Masz nowa wiadomosc'}
                </div>
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); closeNotification(); }}
              style={{
                background: 'var(--bg-secondary)',
                
                color: '#fff' ,
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ✕
            </button>
          </div>

          {/* Message preview */}
          {latestMessage && (
            <div style={{ padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <div style={{
                  width: '28px', height: '28px',
                  background: 'var(--info)', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff' , fontSize: '11px', fontWeight: 600
                }}>
                  {latestMessage.nadawca_login?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '12px', color: 'var(--text)' }}>
                    {latestMessage.nadawca_login}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                    {latestMessage.priorytet === 'pilny' ? 'Pilne' :
                     latestMessage.priorytet === 'wazny' ? 'Wazne' : 'Normalne'}
                  </div>
                </div>
              </div>
              <div style={{ fontWeight: 600, fontSize: '12px', color: 'var(--text)', marginBottom: '3px' }}>
                {latestMessage.tytul}
              </div>
              <div style={{
                fontSize: '11px', color: 'var(--text-muted)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
              }}>
                {latestMessage.tresc?.substring(0, 80)}...
              </div>
            </div>
          )}

          {/* Footer */}
          <div style={{
            padding: '8px 14px',
            borderTop: '1px solid var(--border)',
            background: 'var(--bg)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Kliknij aby otworzyc</span>
            <div style={{
              padding: '4px 10px',
              border: '1px solid var(--primary)', color: 'var(--primary)' ,
              borderRadius: '5px', fontSize: '10px', fontWeight: 600
            }}>
              Otworz wiadomosci
            </div>
          </div>
        </div>
      )}

      {/* Topbar */}
      <div className="topbar">
        <div className="topbar-title">
          {pageTitle}
        </div>
        <div className="topbar-spacer" />
        <div className="topbar-actions">
          <LocationSelector onLocationChange={() => {}} />

          {isPos && <div className="topbar-divider" aria-hidden="true" />}

          {/* Slot POS: nawigacja (zakladki) + narzedzia (skaner, szkic).
              Wypelniany przez PosHeader przez createPortal. */}
          {isPos && (
            <div
              id="pos-topbar-nav"
              style={{ display: 'flex', gap: '6px', alignItems: 'center' }}
            />
          )}

          {isPos && <div className="topbar-divider" aria-hidden="true" />}

          {/* Status + zarzadzanie drukarka fiskalna (jeden wskaznik dla calego systemu) */}
          <FiscalPrinterButton />

          {isPos && <div className="topbar-divider" aria-hidden="true" />}

          {/* Slot POS: otwarcie / zamkniecie zmiany */}
          {isPos && (
            <div
              id="pos-topbar-shift"
              style={{ display: 'flex', gap: '4px', alignItems: 'center' }}
            />
          )}

          {/* Grupa konta (globalna) */}
          <div className="topbar-divider" aria-hidden="true" />

          {/* Przycisk wiadomosci */}
          <button
            className="topbar-btn"
            onClick={() => navigate('/wiadomosci')}
            title={unreadMessages > 0 ? `${unreadMessages} NIEPRZECZYTANYCH WIADOMOSCI!` : 'Wiadomosci'}
            style={unreadMessages > 0 ? {
              animation: 'urgentBg 0.6s ease-in-out infinite',
              borderRadius: '8px',
            } : undefined}
          >
            <i
              className="fas fa-envelope"
              style={unreadMessages > 0 ? {
                animation: 'urgentBlink 0.6s ease-in-out infinite',
                fontSize: '18px',
              } : undefined}
            />
            {unreadMessages > 0 && (
              <span
                className="dot"
                style={{
                  width: '14px',
                  height: '14px',
                  top: '2px',
                  right: '2px',
                  fontSize: '9px',
                  fontWeight: 700,
                  color: '#000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'urgentDot 0.6s ease-in-out infinite',
                  lineHeight: 1,
                }}
              >
                {unreadMessages > 9 ? '9+' : unreadMessages}
              </span>
            )}
          </button>

          {/* Przycisk zmiany hasla */}
          {onOpenChangePassword && (
            <button
              className="topbar-btn"
              onClick={onOpenChangePassword}
              title={`Zmień hasło${user?.password_days_left !== null && user?.password_days_left !== undefined && user.password_days_left <= 7 ? ` (wygasa za ${user.password_days_left} dni)` : ''}`}
              style={{
                color: user?.password_days_left !== null && user?.password_days_left !== undefined && user.password_days_left <= 7
                  ? 'var(--warning)' : 'var(--text-secondary)'
              }}
            >
              <i className="fas fa-key" />
              {user?.password_days_left !== null && user?.password_days_left !== undefined && user.password_days_left <= 7 && (
                <span className="dot" />
              )}
            </button>
          )}

          {/* Przycisk wylogowania */}
          <button
            className="topbar-btn"
            onClick={handleLogout}
            title="Wyloguj"
            style={{ color: 'var(--danger)' }}
          >
            <i className="fas fa-sign-out-alt" />
          </button>
        </div>
      </div>
    </>
  );
};

export default Topbar;
